import { useEffect, useState } from "react";
import { Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── RunnerLive (Phase 4, Jeff 10-27 "I want to SEE the process") ────────────
// The live side of a project card while it's in motion: one GET to
// /api/project/<slug>/runner merges (1) the board row's own phase runner,
// (2) per-phase model suggestions from the routing policy ladder, (3) the
// OpenRouter rotation proxy's live budget read, (4) agent lanes in flight.
// Data rules: every mark comes from the card's own payload or a live proxy
// read — nothing inferred; proxies down ≠ broken, the section still shows
// phases (the board payload is the truth it never needs a network for).

const StatusWord = {
  done: "Done",
  todo: "Not started",
};

const statusTone = (status, isCurrent) => {
  if (status === "done") return "text-os-green";
  if (isCurrent) return "text-os-amber";
  return "text-os-ink-3";
};

// One phase row: number, title, status word, and the model lane it suggests.
const PhaseLine = ({ ph }) => (
  <li
    key={ph.n}
    data-testid={`runner-phase-${ph.n}`}
    data-status={ph.status}
    className="flex flex-col gap-0.5 py-1.5"
  >
    <div className="flex items-baseline justify-between gap-2">
      <span
        className={cn(
          "text-[0.875rem] leading-[1.45]",
          ph.isCurrent ? "font-medium text-os-ink" : "text-os-ink-2",
        )}
      >
        {ph.n}. {ph.title}
      </span>
      <span
        data-testid={`runner-phase-status-${ph.n}`}
        className={cn(
          "shrink-0 font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
          statusTone(ph.status, ph.isCurrent),
        )}
      >
        {ph.status === "done" ? "done" : ph.isCurrent ? "running" : "queued"}
      </span>
    </div>
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        data-testid={`runner-phase-model-${ph.n}`}
        className={cn(
          "whitespace-nowrap rounded-chip border px-1.5 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em]",
          ph.status === "done"
            ? "border-os-border text-os-ink-3"
            : "border-os-border-strong text-os-amber",
        )}
      >
        {ph.modelPrimary}
      </span>
      <span className="font-mono text-[0.5625rem] text-os-ink-3">
        fallback: {ph.modelFallback}
      </span>
    </div>
    {ph.update && (
      <p className="text-[0.8125rem] leading-[1.45] text-os-ink-2">{ph.update}</p>
    )}
  </li>
);

const fmtMoney = (n) =>
  n == null ? "—" : n >= 1 ? `$${Number(n).toFixed(2)}` : `$${Number(n).toFixed(4)}`;

const BudgetLine = ({ budget }) => {
  const spent = budget.weeklySpent;
  const cap = budget.weeklyCap;
  const pct = cap ? Math.round((100 * spent) / cap) : null;
  return (
    <p data-testid="runner-budget" className="text-[0.875rem] leading-[1.45] text-os-ink-2">
      Lanes this week: <span className="font-mono text-os-ink">{fmtMoney(spent)}</span>
      {cap != null ? <> of {fmtMoney(cap)} cap</> : null}
      {pct != null ? <span className="text-os-ink-3"> ({pct}%)</span> : null}
      {" · "}
      {budget.subCap != null && (
        <>paid-tier sub-cap {fmtMoney(budget.subCap)}/wk</>
      )}
    </p>
  );
};

const ConcurrencyLine = ({ con }) => {
  const bits = [];
  if (con.ollamaLimit != null) bits.push(`cloud proxy ${con.ollamaInFlight ?? 0}/${con.ollamaLimit} in flight`);
  if (con.ollamaCooling != null) bits.push(`${con.ollamaCooling} keys cooling`);
  bits.push(`${con.agentLanesActive} agent lane${con.agentLanesActive === 1 ? "" : "s"} active`);
  return (
    <p data-testid="runner-concurrency" className="text-[0.8125rem] leading-[1.45] text-os-ink-3">
      {bits.join(" · ")}
    </p>
  );
};

const LanesRow = ({ lanes }) =>
  lanes.length === 0 ? null : (
    <section data-testid="runner-lanes" className="mt-3">
      <Label as="h4" className="!text-[0.6875rem]">
        Agent lanes on this project
      </Label>
      <ul className="mt-1 flex flex-col gap-1">
        {lanes.map((l) => (
          <li key={l.deleg} className="font-mono text-[0.6875rem] text-os-ink-2">
            {l.deleg} — {l.goalFirst}({l.tasksRunning} task{l.tasksRunning === 1 ? "" : "s"})
          </li>
        ))}
      </ul>
    </section>
  );

const RunnerSkeleton = () => (
  <p data-testid="runner-live-loading" className="text-[0.875rem] text-os-ink-3">
    Reading the live feeds…
  </p>
);

const RunnerFailed = ({ retry }) => (
  <div data-testid="runner-live-failed" className="flex flex-col gap-1.5">
    <p className="text-[0.875rem] text-os-ink-2">
      The live feed isn't answering — the phases above are still the truth.
    </p>
    <button
      type="button"
      onClick={retry}
      className="self-start rounded-control border border-os-border px-2.5 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink"
    >
      Try again
    </button>
  </div>
);

export const RunnerLive = ({ slug }) => {
  const [runner, setRunner] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    fetch(`/api/project/${encodeURIComponent(slug)}/runner`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body) => {
        if (alive) {
          setRunner(body);
          setFailed(false);
        }
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [slug, tick]);

  if (failed) return <RunnerFailed retry={() => setTick((t) => t + 1)} />;
  if (!runner) return <RunnerSkeleton />;

  return (
    <section data-testid="runner-live" className="border-b border-os-border py-4">
      <div className="flex items-baseline justify-between gap-2">
        <Label as="h3">Live right now</Label>
        <MonoMeta as="span">phase {runner.current} of {runner.phaseCount}</MonoMeta>
      </div>
      <ol className="mt-1.5 flex flex-col divide-y divide-os-border/60">
        {runner.phases.map((ph) => (
          <PhaseLine key={ph.n} ph={ph} />
        ))}
      </ol>
      <div className="mt-3 flex flex-col gap-1 border-t border-os-border pt-3">
        <BudgetLine budget={runner.budget} />
        <ConcurrencyLine con={runner.concurrency} />
      </div>
      <LanesRow lanes={runner.lanesOnThisProject || []} />
    </section>
  );
};

export default RunnerLive;